import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { createQuiz, updateQuiz } from '../vsecuriteSlice';
import { showSuccessToast, showErrorToast } from '../../../app/utils/helpers';
import Langs, { Localize } from '../../../app/lang/langs';

const BRAND_ORANGE = '#FF9015';
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

/** Shared input sx for 16px font on mobile (prevents iOS zoom) */
const mobileInputSx = (isMob) => isMob ? { '& input': { fontSize: '16px' }, '& textarea': { fontSize: '16px' } } : {};

/**
 * QuizForm – Admin form for creating or editing a V-Sécurité quiz.
 *
 * @param {object|null}   props.quiz       - Existing quiz object (edit mode), or null (create mode).
 * @param {Array}         props.campaigns  - List of awareness campaigns for optional linking.
 * @param {Array}         props.teams      - List of teams for targeting.
 * @param {Function}      props.onSave     - Callback fired after a successful save.
 * @param {Function}      props.onCancel   - Callback fired when the user cancels.
 */
const QuizForm = ({ quiz, campaigns = [], teams = [], collabs = [], onSave, onCancel }) => {
    const dispatch = useDispatch();
    const lang     = useSelector((state) => state.settings.lang);
    const isMobileRedux = useSelector((state) => state.settings.isMobile);
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const mobile = isMobileRedux === 2 || isMobileScreen;

    const [titre, setTitre]                = useState('');
    const [description, setDescription]   = useState('');
    const [passingScore, setPassingScore]  = useState(90);
    const [targetType, setTargetType]      = useState('all');
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [selectedCollabs, setSelectedCollabs] = useState([]);
    const [campaignId, setCampaignId]      = useState('');
    const [dateLimite, setDateLimite]      = useState('');
    const [allowCorrections, setAllowCorrections] = useState(false);
    const [allowScores, setAllowScores]    = useState(false);
    const [allowMultipleTakes, setAllowMultipleTakes] = useState(false);
    const [teamSearch, setTeamSearch]      = useState('');
    const [collabSearch, setCollabSearch]  = useState('');
    const [submitted, setSubmitted]        = useState(false);
    const [submitting, setSubmitting]      = useState(false);

    /* ── Populate fields in edit mode ── */
    useEffect(() => {
        if (quiz) {
            setTitre(quiz.titre || quiz.title || '');
            setDescription(quiz.description || '');
            setPassingScore(quiz.min_score ?? quiz.passing_score ?? quiz.score_minimum ?? 90);
            setTargetType(quiz.target_type || 'all');
            setSelectedTeams(
                Array.isArray(quiz.team_ids)
                    ? quiz.team_ids
                    : Array.isArray(quiz.teams)
                        ? quiz.teams.map((t) => (typeof t === 'object' ? t.id : t))
                        : []
            );
            setSelectedCollabs(
                Array.isArray(quiz.collab_ids) ? quiz.collab_ids.map(Number) : []
            );
            setCampaignId(quiz.id_awareness_campaign || quiz.id_campaign || quiz.campaign_id || '');
            setDateLimite(quiz.deadline || quiz.date_limite || '');
            setAllowCorrections(Boolean(quiz.allow_corrections));
            setAllowScores(Boolean(quiz.allow_scores));
            setAllowMultipleTakes(Boolean(quiz.allow_multiple_takes));
        }
    }, [quiz]);

    /* ── Validation ── */
    const titreError = submitted && !titre.trim();
    const scoreError = submitted && (passingScore < 50 || passingScore > 100);
    const teamsError = submitted && targetType === 'teams' && selectedTeams.length === 0 && selectedCollabs.length === 0;

    const isFormValid =
        titre.trim().length > 0 &&
        passingScore >= 50 &&
        passingScore <= 100 &&
        (targetType === 'all' || selectedTeams.length > 0 || selectedCollabs.length > 0);

    const isPublishedQuiz = quiz?.status === 'published' || quiz?.statut === 'published';

    /* ── Submit ── */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isPublishedQuiz) {
            setSubmitted(true);
            if (!isFormValid) return;
        }

        const payload = {
            title:                 titre.trim(),
            description:           description.trim(),
            min_score:             passingScore,
            target_type:           targetType,
            team_ids:              targetType === 'teams' ? selectedTeams : [],
            collab_ids:            targetType === 'teams' ? selectedCollabs : [],
            id_awareness_campaign: campaignId || null,
            deadline:              dateLimite || null,
            allow_corrections:     allowCorrections,
            allow_scores:          allowScores,
            allow_multiple_takes:  allowMultipleTakes,
        };

        setSubmitting(true);
        try {
            if (quiz?.id) {
                await dispatch(updateQuiz({ id: quiz.id, ...payload })).unwrap();
                showSuccessToast(Localize('VSEC_TOAST_QUIZ_UPDATED', lang));
            } else {
                await dispatch(createQuiz(payload)).unwrap();
                showSuccessToast(Localize('VSEC_TOAST_QUIZ_CREATED', lang));
            }
            onSave();
        } catch {
            showErrorToast(Localize('VSEC_TOAST_QUIZ_SAVE_ERROR', lang));
        } finally {
            setSubmitting(false);
        }
    };

    const isEditMode = Boolean(quiz?.id);

    /* ── Reusable toggle row ── */
    const ToggleRow = ({ checked, onChange, labelKey, hintKey }) => (
        <Box
            sx={{
                mb: 3,
                px: 2,
                py: 1.5,
                border: '1px solid rgba(0,0,0,0.10)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                background: checked ? 'rgba(255,144,21,0.04)' : 'transparent',
                transition: 'background 200ms',
                minHeight: 56,
            }}
        >
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#222' }}>
                    <Langs str={labelKey} />
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#777', mt: 0.25 }}>
                    <Langs str={hintKey} />
                </Typography>
            </Box>
            <Switch
                checked={checked}
                onChange={onChange}
                sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND_ORANGE },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: BRAND_ORANGE },
                }}
            />
        </Box>
    );

    /* ── Published-only locked form ── */
    if (isPublishedQuiz) {
        return (
            <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{
                    background: '#fff',
                    borderRadius: mobile ? 0 : '14px',
                    border: mobile ? 'none' : '1px solid rgba(0,40,128,0.07)',
                    maxWidth: mobile ? '100%' : 720,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: mobile ? '100%' : 'calc(100vh - 200px)',
                    overflow: 'hidden',
                }}
            >
                {/* Sticky header */}
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2, flexShrink: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>
                        <Langs str="VSEC_TOOLTIP_QUIZ_SETTINGS" />
                    </Typography>
                    <Divider sx={{ mt: 2 }} />
                </Box>

                {/* Scrollable body */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: { xs: 2, sm: 3 },
                        pb: 2,
                        WebkitOverflowScrolling: 'touch',
                        '&::-webkit-scrollbar': { width: '5px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(255,144,21,0.25)',
                            borderRadius: '3px',
                        },
                    }}
                >
                    {/* Published/locked info banner */}
                    <Box
                        sx={{
                            mt: 2,
                            mb: 3,
                            px: 2,
                            py: 1.5,
                            borderRadius: '10px',
                            background: 'rgba(255,144,21,0.08)',
                            border: `1px solid rgba(255,144,21,0.30)`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <LockOutlinedIcon sx={{ color: BRAND_ORANGE, fontSize: '1.2rem', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.875rem', color: '#7a4a00', fontWeight: 500 }}>
                            <Langs str="VSEC_QUIZ_PUBLISHED_LOCKED" />
                        </Typography>
                    </Box>

                    <ToggleRow checked={allowCorrections} onChange={(e) => setAllowCorrections(e.target.checked)} labelKey="VSEC_FIELD_ALLOW_CORRECTIONS" hintKey="VSEC_FIELD_ALLOW_CORRECTIONS_HINT" />
                    <ToggleRow checked={allowScores} onChange={(e) => setAllowScores(e.target.checked)} labelKey="VSEC_FIELD_ALLOW_SCORES" hintKey="VSEC_FIELD_ALLOW_SCORES_HINT" />
                    <ToggleRow checked={allowMultipleTakes} onChange={(e) => setAllowMultipleTakes(e.target.checked)} labelKey="VSEC_FIELD_ALLOW_MULTIPLE_TAKES" hintKey="VSEC_FIELD_ALLOW_MULTIPLE_TAKES_HINT" />

                    {/* Deadline (future only) */}
                    <TextField
                        label={Localize('VSEC_FIELD_DEADLINE', lang)}
                        type="date"
                        value={dateLimite}
                        onChange={(e) => setDateLimite(e.target.value)}
                        fullWidth
                        sx={{ mb: 2, ...mobileInputSx(mobile) }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().split('T')[0] }}
                    />
                </Box>

                {/* Sticky footer actions */}
                <Box
                    sx={{
                        flexShrink: 0,
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        borderTop: '1px solid rgba(0,0,0,0.07)',
                        display: 'flex',
                        flexDirection: mobile ? 'column-reverse' : 'row',
                        justifyContent: 'flex-end',
                        gap: mobile ? 1 : 2,
                        background: '#fff',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={submitting}
                        fullWidth={mobile}
                        sx={{ textTransform: 'none', borderRadius: '12px', minHeight: 48, minWidth: 110, transition: `all 250ms ${EASE}`, '&:hover': { transform: 'scale(1.02)' }, '&:active': { transform: 'scale(0.98)' } }}
                    >
                        <Langs str="VSEC_BTN_CANCEL" />
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting}
                        fullWidth={mobile}
                        sx={{
                            backgroundColor: BRAND_ORANGE,
                            color: '#fff',
                            textTransform: 'none',
                            borderRadius: '12px',
                            fontWeight: 600,
                            minHeight: 48,
                            minWidth: 140,
                            '&:hover': { backgroundColor: '#e6820f' },
                            '&.Mui-disabled': { backgroundColor: '#ffc87a', color: '#fff' },
                        }}
                        startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                    >
                        {submitting ? <Langs str="VSEC_BTN_SAVING" /> : <Langs str="VSEC_BTN_SAVE" />}
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
                background: mobile ? '#fff' : 'rgba(255,255,255,0.8)',
                backdropFilter: mobile ? 'none' : 'blur(14px)',
                WebkitBackdropFilter: mobile ? 'none' : 'blur(14px)',
                borderRadius: mobile ? 0 : '20px',
                border: mobile ? 'none' : '1px solid rgba(0,40,128,0.06)',
                boxShadow: mobile ? 'none' : '0 1px 4px rgba(255,144,21,0.04), 0 8px 32px rgba(0,40,128,0.05)',
                maxWidth: mobile ? '100%' : 720,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: mobile ? '100%' : 'calc(100vh - 200px)',
                overflow: 'hidden',
                '@keyframes qfFadeIn': { from: { opacity: 0, transform: 'translateY(12px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                animation: mobile ? 'none' : `qfFadeIn 400ms ${EASE} both`,
            }}
        >
            {/* ── Sticky header ── */}
            <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2, flexShrink: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#222' }}>
                    {isEditMode ? <Langs str="VSEC_QUIZ_FORM_TITLE_EDIT" /> : <Langs str="VSEC_QUIZ_FORM_TITLE_CREATE" />}
                </Typography>
                <Divider sx={{ mt: 2 }} />
            </Box>

            {/* ── Scrollable body ── */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    px: { xs: 2, sm: 3 },
                    pb: 2,
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': { width: '5px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255,144,21,0.25)',
                        borderRadius: '3px',
                    },
                }}
            >
                {/* Title */}
                <TextField
                    label={Localize('VSEC_FIELD_TITLE', lang)}
                    value={titre}
                    onChange={(e) => setTitre(e.target.value)}
                    error={titreError}
                    helperText={titreError ? Localize('VSEC_ERROR_TITLE_QUIZ_REQUIRED', lang) : ''}
                    fullWidth
                    sx={{ mt: 2, mb: 3, ...mobileInputSx(mobile) }}
                    inputProps={{ maxLength: 255 }}
                />

                {/* Description */}
                <TextField
                    label={Localize('VSEC_FIELD_DESCRIPTION', lang)}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ mb: 3, ...mobileInputSx(mobile) }}
                    inputProps={{ maxLength: 1000 }}
                />

                {/* Passing score */}
                <Box sx={{ mb: 3 }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontSize: '0.875rem', fontWeight: 600, display: 'block' }}>
                        <Langs str="VSEC_FIELD_MIN_SCORE" />
                    </FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: mobile ? 2 : 3 }}>
                        <Slider
                            value={passingScore}
                            onChange={(_, val) => setPassingScore(val)}
                            min={50}
                            max={100}
                            step={5}
                            marks
                            valueLabelDisplay="auto"
                            sx={{
                                flex: 1,
                                color: BRAND_ORANGE,
                                '& .MuiSlider-thumb': { backgroundColor: BRAND_ORANGE, width: mobile ? 22 : 16, height: mobile ? 22 : 16 },
                                '& .MuiSlider-track': { backgroundColor: BRAND_ORANGE },
                            }}
                        />
                        <TextField
                            type="number"
                            value={passingScore}
                            onChange={(e) => {
                                const v = Number(e.target.value);
                                if (v >= 50 && v <= 100) setPassingScore(v);
                            }}
                            error={scoreError}
                            inputProps={{ min: 50, max: 100, step: 5, style: { textAlign: 'center', fontSize: mobile ? '16px' : undefined } }}
                            sx={{ width: 80 }}
                            size="small"
                        />
                    </Box>
                    {scoreError && <FormHelperText error>{Localize('VSEC_ERROR_SCORE_RANGE', lang)}</FormHelperText>}
                </Box>

                {/* Target type */}
                <FormControl sx={{ mb: targetType === 'teams' ? 2 : 3 }}>
                    <FormLabel component="legend" sx={{ mb: 0.5, fontSize: '0.875rem', fontWeight: 600 }}>
                        <Langs str="VSEC_FIELD_TARGET_TYPE" />
                    </FormLabel>
                    <RadioGroup row={!mobile} value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                        <FormControlLabel
                            value="all"
                            control={<Radio sx={{ '&.Mui-checked': { color: BRAND_ORANGE } }} />}
                            label={Localize('VSEC_TARGET_ALL', lang)}
                            sx={{ minHeight: 44 }}
                        />
                        <FormControlLabel
                            value="teams"
                            control={<Radio sx={{ '&.Mui-checked': { color: BRAND_ORANGE } }} />}
                            label={Localize('VSEC_TARGET_TEAMS', lang)}
                            sx={{ minHeight: 44 }}
                        />
                    </RadioGroup>
                </FormControl>

                {/* Team multi-select */}
                {targetType === 'teams' && (
                    <FormControl fullWidth error={teamsError} sx={{ mb: 3 }}>
                        <InputLabel id="teams-select-label">
                            <Langs str="VSEC_FIELD_TEAMS_LABEL" />
                        </InputLabel>
                        <Select
                            labelId="teams-select-label"
                            multiple
                            value={selectedTeams}
                            label={<Langs str="VSEC_FIELD_TEAMS_LABEL" />}
                            onChange={(e) => setSelectedTeams(e.target.value)}
                            sx={{
                                fontSize: mobile ? '16px' : undefined,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE },
                            }}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((id) => {
                                        const team = teams.find((t) => t.id === id);
                                        return (
                                            <Chip
                                                key={id}
                                                label={team ? (team.label || team.name || team.nom) : id}
                                                size="small"
                                                sx={{
                                                    background: 'rgba(255,144,21,0.12)',
                                                    color: BRAND_ORANGE,
                                                    fontWeight: 600,
                                                    fontSize: '0.78rem',
                                                    borderRadius: '6px',
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                            MenuProps={{
                                PaperProps: { style: { maxHeight: 320 } },
                                autoFocus: false,
                                sx: { zIndex: 1500 },
                            }}
                            onClose={() => setTeamSearch('')}
                        >
                            <Box sx={{ px: 1.5, pt: 1, pb: 0.5, position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder={Localize('VSEC_SEARCH_TEAMS', lang)}
                                    value={teamSearch}
                                    onChange={(e) => setTeamSearch(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    sx={{ ...mobileInputSx(mobile), '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE } }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <span style={{ fontSize: '1rem' }}>🔍</span>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Box>
                            {teams
                                .filter((t) => {
                                    const name = (t.label || t.name || t.nom || '').toLowerCase();
                                    return name.includes(teamSearch.toLowerCase());
                                })
                                .map((team) => (
                                    <MenuItem key={team.id} value={team.id} sx={{ minHeight: 44 }}>
                                        {team.label || team.name || team.nom}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                        {teamsError && (
                            <FormHelperText>{Localize('VSEC_ERROR_TEAMS_OR_COLLABS_SELECT', lang)}</FormHelperText>
                        )}
                    </FormControl>
                )}

                {/* Individual collab multi-select (optional, alongside teams) */}
                {targetType === 'teams' && (
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel id="collabs-select-label">
                            <Langs str="VSEC_FIELD_COLLABS_LABEL" />
                        </InputLabel>
                        <Select
                            labelId="collabs-select-label"
                            multiple
                            value={selectedCollabs}
                            label={<Langs str="VSEC_FIELD_COLLABS_LABEL" />}
                            onChange={(e) => setSelectedCollabs(e.target.value)}
                            sx={{
                                fontSize: mobile ? '16px' : undefined,
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE },
                            }}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((id) => {
                                        const collab = collabs.find((c) => c.id === id);
                                        const label = collab ? `${collab.prenom} ${collab.nom}` : id;
                                        return (
                                            <Chip
                                                key={id}
                                                label={label}
                                                size="small"
                                                sx={{
                                                    background: 'rgba(255,144,21,0.12)',
                                                    color: BRAND_ORANGE,
                                                    fontWeight: 600,
                                                    fontSize: '0.78rem',
                                                    borderRadius: '6px',
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                            MenuProps={{
                                PaperProps: { style: { maxHeight: 320 } },
                                autoFocus: false,
                                sx: { zIndex: 1500 },
                            }}
                            onClose={() => setCollabSearch('')}
                        >
                            <Box sx={{ px: 1.5, pt: 1, pb: 0.5, position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder={Localize('VSEC_SEARCH_COLLABS', lang)}
                                    value={collabSearch}
                                    onChange={(e) => setCollabSearch(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    sx={{ ...mobileInputSx(mobile), '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_ORANGE } }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <span style={{ fontSize: '1rem' }}>🔍</span>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Box>
                            {collabs
                                .filter((c) => {
                                    const q = collabSearch.toLowerCase();
                                    return !q
                                        || (c.prenom || '').toLowerCase().includes(q)
                                        || (c.nom || '').toLowerCase().includes(q)
                                        || (c.email || '').toLowerCase().includes(q);
                                })
                                .map((c) => (
                                    <MenuItem key={c.id} value={c.id} sx={{ minHeight: 44 }}>
                                        {c.prenom} {c.nom}
                                    </MenuItem>
                                ))
                            }
                        </Select>
                        <FormHelperText>{Localize('VSEC_FIELD_COLLABS_HELPER', lang)}</FormHelperText>
                    </FormControl>
                )}

                {/* Linked campaign (optional) */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="campaign-select-label">
                        {Localize('VSEC_FIELD_CAMPAIGN_LABEL', lang)}
                    </InputLabel>
                    <Select
                        labelId="campaign-select-label"
                        value={campaignId}
                        label={Localize('VSEC_FIELD_CAMPAIGN_LABEL', lang)}
                        onChange={(e) => setCampaignId(e.target.value)}
                        sx={{ fontSize: mobile ? '16px' : undefined }}
                        MenuProps={{ sx: { zIndex: 1500 } }}
                    >
                        <MenuItem value="">
                            <em>{Localize('VSEC_OPTION_NONE', lang)}</em>
                        </MenuItem>
                        {campaigns.map((c) => (
                            <MenuItem key={c.id} value={c.id} sx={{ minHeight: 44 }}>
                                {c.titre || c.title}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Toggle switches */}
                <ToggleRow checked={allowCorrections} onChange={(e) => setAllowCorrections(e.target.checked)} labelKey="VSEC_FIELD_ALLOW_CORRECTIONS" hintKey="VSEC_FIELD_ALLOW_CORRECTIONS_HINT" />
                <ToggleRow checked={allowScores} onChange={(e) => setAllowScores(e.target.checked)} labelKey="VSEC_FIELD_ALLOW_SCORES" hintKey="VSEC_FIELD_ALLOW_SCORES_HINT" />
                <ToggleRow checked={allowMultipleTakes} onChange={(e) => setAllowMultipleTakes(e.target.checked)} labelKey="VSEC_FIELD_ALLOW_MULTIPLE_TAKES" hintKey="VSEC_FIELD_ALLOW_MULTIPLE_TAKES_HINT" />

                {/* Deadline (optional) */}
                <TextField
                    label={Localize('VSEC_FIELD_DEADLINE', lang)}
                    type="date"
                    value={dateLimite}
                    onChange={(e) => setDateLimite(e.target.value)}
                    fullWidth
                    sx={{ mb: 2, ...mobileInputSx(mobile) }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                />
            </Box>

            {/* ── Sticky footer actions ── */}
            <Box
                sx={{
                    flexShrink: 0,
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    borderTop: '1px solid rgba(0,0,0,0.07)',
                    display: 'flex',
                    flexDirection: mobile ? 'column-reverse' : 'row',
                    justifyContent: 'flex-end',
                    gap: mobile ? 1 : 2,
                    background: mobile ? '#fff' : 'rgba(255,255,255,0.85)',
                    backdropFilter: mobile ? 'none' : 'blur(8px)',
                    WebkitBackdropFilter: mobile ? 'none' : 'blur(8px)',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onCancel}
                    disabled={submitting}
                    fullWidth={mobile}
                    sx={{ textTransform: 'none', borderRadius: '12px', minHeight: 48, minWidth: 110, transition: `all 250ms ${EASE}`, '&:hover': { transform: 'scale(1.02)' }, '&:active': { transform: 'scale(0.98)' } }}
                >
                    <Langs str="VSEC_BTN_CANCEL" />
                </Button>

                <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting}
                    fullWidth={mobile}
                    sx={{
                        backgroundColor: BRAND_ORANGE,
                        color: '#fff',
                        textTransform: 'none',
                        borderRadius: '12px',
                        fontWeight: 600,
                        minHeight: 48,
                        minWidth: 140,
                        boxShadow: '0 2px 8px rgba(255,144,21,0.25)',
                        transition: `all 250ms ${EASE}`,
                        '&:hover': { backgroundColor: '#e6820f', boxShadow: '0 6px 20px rgba(255,144,21,0.35)', transform: 'translateY(-1px) scale(1.02)' },
                        '&:active': { transform: 'scale(0.98)' },
                        '&.Mui-disabled': { backgroundColor: '#ffc87a', color: '#fff' },
                    }}
                    startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                >
                    {submitting ? <Langs str="VSEC_BTN_SAVING" /> : <Langs str="VSEC_BTN_SAVE" />}
                </Button>
            </Box>
        </Box>
    );
};

export default QuizForm;
